import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { TimeSelect } from '../ui/TimeSelect';
import { Trash2 } from 'lucide-react';

export const AddSaleDialog = ({ open, onOpenChange, systemSales, onJoinSale }) => {
  // Sort: already-added at top by start date asc, then not-added by start date asc
  const sortedSales = [...systemSales].sort((a, b) => {
    if (a.is_linked !== b.is_linked) return a.is_linked ? -1 : 1;
    const aDate = a.sale_start_date ? new Date(a.sale_start_date) : new Date(0);
    const bDate = b.sale_start_date ? new Date(b.sale_start_date) : new Date(0);
    return aDate - bDate;
  });

  const addedSales = sortedSales.filter(s => s.is_linked);
  const notAddedSales = sortedSales.filter(s => !s.is_linked);

  const renderSaleRow = (sale) => {
    const alreadyAdded = sale.is_linked;
    const startDate = sale.sale_start_date ? new Date(sale.sale_start_date).toLocaleDateString() : '-';
    const endDate = sale.sale_end_date ? new Date(sale.sale_end_date).toLocaleDateString() : '-';
    return (
      <div
        key={sale.id}
        className={`p-4 border rounded ${alreadyAdded ? 'bg-muted/30' : 'hover:bg-muted/50 cursor-pointer'}`}
        onClick={() => !alreadyAdded && onJoinSale(sale)}
      >
        <div className="flex items-start justify-between">
          <div>
            <p className="font-medium">{sale.sale_name}</p>
            <p className="text-sm text-muted-foreground">
              {sale.sale_company && <>{sale.sale_company} &bull; </>}
              {startDate} &ndash; {endDate}
              {sale.location && <> &bull; {sale.location}</>}
            </p>
          </div>
          {alreadyAdded ? (
            <Badge variant="secondary">Already Added</Badge>
          ) : (
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onJoinSale(sale); }}>
              Add
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Sale to Your Active Sales</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-sm text-muted-foreground mb-4">
            Select a system-wide sale to add to your consignment calendar
          </p>
          {systemSales.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No system sales available</p>
          ) : (
            <div className="space-y-2">
              {addedSales.length > 0 && (
                <>
                  {addedSales.map(renderSaleRow)}
                  {notAddedSales.length > 0 && (
                    <div className="flex items-center gap-2 py-1">
                      <div className="flex-1 border-t" />
                      <span className="text-xs text-muted-foreground">Available to Add</span>
                      <div className="flex-1 border-t" />
                    </div>
                  )}
                </>
              )}
              {notAddedSales.map(renderSaleRow)}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SessionDialog = ({
  open, onOpenChange, sessionForm, setSessionForm, selectedSale, editingSession, saving, onSave, onDelete
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{editingSession ? 'Edit Session' : 'Create Sale Session'}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4 py-4">
        <div className="space-y-2">
          <Label>Session Name *</Label>
          <Input
            value={sessionForm.name}
            onChange={(e) => setSessionForm({...sessionForm, name: e.target.value})}
            placeholder="e.g., Session 1, Day 1"
          />
        </div>
        <div className="space-y-2">
          <Label>Session Date *</Label>
          <Input
            type="date"
            value={sessionForm.date}
            onChange={(e) => setSessionForm({...sessionForm, date: e.target.value})}
            className={sessionForm.date ? 'date-filled' : ''}
            min={selectedSale?.sale_start_date ? new Date(selectedSale.sale_start_date).toISOString().split('T')[0] : selectedSale?.start_date ? new Date(selectedSale.start_date).toISOString().split('T')[0] : undefined}
            max={selectedSale?.sale_end_date ? new Date(selectedSale.sale_end_date).toISOString().split('T')[0] : selectedSale?.end_date ? new Date(selectedSale.end_date).toISOString().split('T')[0] : undefined}
          />
          {selectedSale && (
            <p className="text-xs text-muted-foreground">
              Must be between {new Date(selectedSale.sale_start_date || selectedSale.start_date).toLocaleDateString()} - {new Date(selectedSale.sale_end_date || selectedSale.end_date).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label>Ship In Date *</Label>
          <Input
            type="date"
            value={sessionForm.ship_in_date}
            onChange={(e) => setSessionForm({...sessionForm, ship_in_date: e.target.value})}
            className={sessionForm.ship_in_date ? 'date-filled' : ''}
            max={sessionForm.date || undefined}
          />
          <p className="text-xs text-muted-foreground">When horses will be shipped in (typically before session date)</p>
        </div>
        <div className="space-y-2">
          <Label>Barn</Label>
          <Input
            value={sessionForm.barn}
            onChange={(e) => setSessionForm({...sessionForm, barn: e.target.value})}
            placeholder="e.g., Barn 42, Section A"
          />
        </div>
        <div className="space-y-2">
          <Label>Ship In Time</Label>
          <TimeSelect
            value={sessionForm.time}
            onChange={(v) => setSessionForm({...sessionForm, time: v})}
          />
        </div>
      </div>
      <DialogFooter className="flex items-center">
        {editingSession && onDelete && (
          <button
            onClick={onDelete}
            className="mr-auto text-muted-foreground hover:text-destructive transition-colors p-1"
            title="Delete session"
            type="button"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onSave} disabled={saving} className="bg-primary hover:bg-primary/90 text-white">
          {saving ? 'Saving...' : (editingSession ? 'Update Session' : 'Create Session')}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ArchiveSaleDialog = ({ open, onOpenChange, saleToArchive, onArchive }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Archive Sale</DialogTitle>
      </DialogHeader>
      <div className="py-4">
        <p className="text-sm text-muted-foreground mb-2">
          Archive <strong>{saleToArchive?.sale_name}</strong>? It will be moved to Historic Sales.
        </p>
        <p className="text-sm text-muted-foreground">
          All sale opportunities for this sale will remain accessible.
        </p>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={onArchive} className="bg-blue-600 hover:bg-blue-700 text-white">Archive Sale</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
